import { Component, ElementRef, Input, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { appSettings } from "src/app/app.settings";
import {
  IProductCard,
  IProductCardFormData,
  IProductConformityInfo
} from "src/app/cards/product/product-info/product-info.model";
import { ALLERGEN, FOOD_SUGGESTION } from "src/app/config";
import { PICTOGRAMS } from "src/app/config/constants";
import { ProductValidationDialogComponent } from "src/app/dialogs";
import { ProductCloneDialogComponent } from "src/app/dialogs/product-clone/product-clone.dialog";
import {
  LxmAppModule,
  ProductUnitType,
  SupplierProductAssortmentDeliveryStatus,
  SupplierProductAssortmentStatus,
  UserAction
} from "src/app/enum";
import { ProductUnitCodeType } from "src/app/enum/product-unit-code-type";
import { AppState } from "src/app/state/app.state";
import { LxmMessage } from "src/app/_helpers";
import { ActionHelper } from "src/app/_helpers/action";
import { ProductService, ProductValidity, SignalRService } from "src/app/_services";
import { ProductHeaderState } from "./product-header.state";
import svgIcons from "src/assets/svg/svg-icons";
import { BreadcrumbService } from "xng-breadcrumb";
import { TranslatedValuePipe } from "src/app/pipes";
import { ExportProductsPdfDialog } from "src/app/dialogs/export-products-pdf/export-products-pdf.dialog";
import { ExportProductsLabelsDialog } from "src/app/dialogs/export-products-labels/export-products-labels.dialog";
import { map, mergeMap } from "rxjs/operators";
import { HubConnection } from "@microsoft/signalr";

@Component({
  selector: "product-header-card",
  templateUrl: "./product-header.card.html",
  styleUrls: ["./product-header.card.scss"]
})
export class ProductHeaderCard {
  @ViewChild("download") private _download: ElementRef;

  public edit = false;

  public imagesListCount = 4;

  public hasBuyerPimExtensions = false;

  public isOwnProduct = true;
  public isClone = false;

  public isProductActive: boolean;

  private _formData: IProductCardFormData;

  public UserAction = UserAction;

  @Input() public isMarketProduct: boolean = false;

  @Input() public productId: string;

  public showDeleteButton = false;
  public showAddButton = false;
  public showCloneButton = false;
  public showPdfButton = false;
  public showLabelsButton = false;
  public sendToDirectoButtonDisabled = false;
  public sendForConformityConfirmationButtonDisabled = false;

  public show = {
    dotMenu: false,
    deleteButton: false,
    addButton: false,
    cloneButton: false,
    pdfButton: false,
    labelsButton: false,
    sendToDirectoButton: false,
    validateButton: false,
    conformityInfo: false,
    confirmConformityButton: false,
    sendForConformityConfirmationButton: false,

    //Temporary to show that pictures can be downloaded from Apollo media server. todo - remove.
    getImageFromApollo: false
  };

  public pictograms: string[] = [];

  public showRetailerAssortmentStatus = false;

  private _updateSubscription: Subscription;

  private __signalRSubscription: Subscription;
  private __hubConnection: HubConnection;

  constructor(
    public state: ProductHeaderState,
    private _appState: AppState,
    private route: ActivatedRoute,
    private _router: Router,
    private _action: ActionHelper,
    private _productService: ProductService,
    private _productValidity: ProductValidity,
    private _lxmMessage: LxmMessage,
    private _breadcrumbService: BreadcrumbService,
    private _translatedValuePipe: TranslatedValuePipe,
    private _signalRService: SignalRService
  ) {
    const productCard = route.snapshot.data.productCard as IProductCard;
    this._formData = productCard.formData;
    this.isClone = productCard.isClone;

    const { productId, action } = this.route.snapshot.params;

    const {
      baseInfo,
      additionalInfo,
      expirationInfo,
      images,
      markingAndNutritionInfo,
      alcoholInfo,
      retailerAssortments,
      assortments,
      validationResults,
      productInfoFullness,
      conformityInfo
    } = productCard || {};

    this.sendToDirectoButtonDisabled = productInfoFullness < 75;
    this.isProductActive = productCard.isActive;
    this.isOwnProduct = productCard.isOwnProduct;

    this.showRetailerAssortmentStatus =
      this.isOwnProduct &&
      this._appState.hasModule(LxmAppModule.BuyerAssortment);

    if (this._productValidity.hasBuyerPimExtensions) {
      this._productValidity.shouldValidate = true;
      this._productValidity.set(validationResults);
    }

    this._breadcrumbService.set(
      "@ProductName",
      this._translatedValuePipe.transform(baseInfo?.fullName)
    );

    const currentSupplierAssortment = assortments?.length
      ? assortments?.find((x) => x.isCurrent)
      : null;

    this.state.setData({
      id: productId,
      fullName: baseInfo?.fullName,
      images: images?.sort((a, b) => (a.isMaster ? -1 : 1)) ?? [],
      ean13: baseInfo.codes.find(
        (x) => x.type?.id === ProductUnitCodeType.EAN13 && x.isPrimary
      ),
      supplierCode: baseInfo.codes.find(
        (x) => x.type?.id === ProductUnitCodeType.SupplierCode && x.isPrimary
      ),
      internalCode: baseInfo.codes.find(
        (x) => x.type?.id === ProductUnitCodeType.InternalCode && x.isPrimary
      ),
      weighingGoodsCode: baseInfo.codes.find(
        (x) => x.type?.id === ProductUnitCodeType.WeighingGoods && x.isPrimary
      ),
      brand: baseInfo?.brand,
      countryOfOrigin: baseInfo?.countryOfOrigin,
      markingAndNutritionInfo: markingAndNutritionInfo,
      foodSuggestions: alcoholInfo?.wineFoodSuggestions,
      retailerAssortmentStatus: retailerAssortments?.length
        ? retailerAssortments?.find((x) => x.isCurrent)?.status
        : null,
      supplierAssortmentStatus: currentSupplierAssortment?.status,
      supplierAssortmentDeliveryStatus: currentSupplierAssortment?.deliveryStatus,
      awards: additionalInfo?.awards?.map((x) => x.id),
      storageConditionId: expirationInfo?.storageConditions?.id,
      alcoholContent: alcoholInfo?.alcoholContent,
      alcoholFree: markingAndNutritionInfo?.alcoholFree,
      ethicalMarkingIds: markingAndNutritionInfo?.ethicalMarkings?.map((x) => x.id),
      conformityInfo: conformityInfo,
      isEudrProduct: baseInfo?.isEudrProduct
    });

    this.__signalRSubscription = this._signalRService.commonHub.subscribe(x => {
      this.__hubConnection = x;

      if (x === null) {
        return;
      }

      x.on('productConformityInfoChanged', (data: any) => {
        this.state.setData({ conformityInfo: data });
        if (data?.isConfirmed) {
          this.show.sendForConformityConfirmationButton = false;
        } else {
          this.show.sendForConformityConfirmationButton = (this._formData.conformityDeclarationSettings?.canSendForConfirmation ?? false);
        }
      });
    });
  }

  public get conformityInfo(): IProductConformityInfo {
    return this.data?.conformityInfo;
  }

  public get productActiveStatus(): SupplierProductAssortmentStatus {
    return this.data?.status;
  }

  public get retailerAssortmentStatus() {
    return this.data?.retailerAssortmentStatus;
  }

  public get supplierAssortmentStatus() {
    return (
      this.data?.supplierAssortmentStatus ??
      SupplierProductAssortmentStatus.NoScheme
    );
  }

  public get supplierAssortmentDeliveryStatus() {
    return this.data?.supplierAssortmentDeliveryStatus ?? SupplierProductAssortmentDeliveryStatus.DeliveryAllowed;
  }

  public getCountryIcon(item: any) {
    if (!item["iso3"]) {
      return;
    }
    const key = item["iso3"].toLowerCase();
    if (key) {
      if (svgIcons[key]) {
        return key;
      }
    }
    if (svgIcons["global"]) {
      return "global";
    }
    return;
  }

  ngOnInit() {
    this._setRights();
    this._updatePictograms();
    this.hasBuyerPimExtensions = this._appState.hasModule(
      LxmAppModule.BuyerPimExtensions
    );

    const { action } = this.route.snapshot.params;
    if (action) {
      switch (action) {
        case "validate":
          if (this.show.validateButton) {
            const { retailerId } = this.route.snapshot.queryParams;
            this.openValidationDialog(true, retailerId);
          }

          break;
      }
    }
  }

  public isChilled(storageConditionId) {
    const chilledId = "862d0c3f-ce13-4fa8-baac-88ea50e2ff8b";
    return storageConditionId === chilledId;
  }

  public isFrozen(storageConditionId) {
    const frozenId = "10402d42-aacc-44a9-bbe2-ee0a10ea9816";
    return storageConditionId === frozenId;
  }

  private _updatePictograms() {
    const allergens = this.allergens.map(
      (x: { allergen: { code: any } }) => x.allergen?.code
    );
    let pictograms = [];

    const {
      suitableForVegans,
      lactoseFree,
      sugarFree,
      glutenFree,
      alcoholFree
    } = this.nutritionInfo || {};

    // Check for eudr marking
    if (this.data?.isEudrProduct) {
      pictograms.push(PICTOGRAMS.eudr);
    }

    // Check if has any awards
    if (this.awards?.length) {
      pictograms.push(PICTOGRAMS.awarded);
    }

    // Check for ethical markings
    const ethicalMarkingIds = this.data?.ethicalMarkingIds;
    if (ethicalMarkingIds?.length) {
      // Fairtrade
      const fairtradeMarkingIds = [
        "151fdf82-fd74-4d0c-816d-e2a136e3ef09",
        "1fc596c3-3a08-4b5c-8a9b-b52d3d01b703",
        "1706dc2d-4927-4fba-bfda-71995dab96e3"
      ];

      const fairtrade = ethicalMarkingIds.some((x) =>
        fairtradeMarkingIds.includes(x)
      );

      if (fairtrade) {
        pictograms.push(PICTOGRAMS.fairtrade);
      }

      // EU Organic farming
      const organicFarmingMarkingIds = ["3c34c762-56eb-4de1-a9dd-f9deafb0a631"];

      const organicFarming = ethicalMarkingIds.some((x) =>
        organicFarmingMarkingIds.includes(x)
      );

      if (organicFarming) {
        pictograms.push(PICTOGRAMS.organicFarming);
      }

      // Rainforest alliance
      const rainforestAllianceMarkingIds = [
        "23b46aeb-d590-47fa-be18-6dedfef101d0",
        "abc3e861-0285-4b82-a9ba-4cffff1b747a"
      ];

      const rainforestAlliance = ethicalMarkingIds.some((x) =>
        rainforestAllianceMarkingIds.includes(x)
      );

      if (rainforestAlliance) {
        pictograms.push(PICTOGRAMS.rainforestAlliance);
      }

      // Tunnustatud eesti maitse - Pääsukesemärk
      const eestiMaitseMarkingIds = ["cd4d8a2e-0073-4036-8ce7-9ac726b22959"];

      const eestiMaitse = ethicalMarkingIds.some((x) =>
        eestiMaitseMarkingIds.includes(x)
      );

      if (eestiMaitse) {
        pictograms.push(PICTOGRAMS.tunnustatudEestiMaitse);
      }
    }

    // Check for storage conditions
    const storageConditionId = this.data?.storageConditionId;
    if (storageConditionId) {
      if (this.isChilled(storageConditionId)) {
        pictograms.push(PICTOGRAMS.chilled);
      }

      if (this.isFrozen(storageConditionId)) {
        pictograms.push(PICTOGRAMS.frozen);
      }
    }

    // Check for vegan
    if (suitableForVegans) {
      pictograms.push(PICTOGRAMS.suitableForVegans);
    }

    // Check for gluten free
    if (glutenFree) {
      pictograms.push(PICTOGRAMS.glutenFree);
    }

    // Check for lactose free
    if (lactoseFree) {
      pictograms.push(PICTOGRAMS.lactoseFree);
    }

    // Check for sugar free
    if (sugarFree) {
      pictograms.push(PICTOGRAMS.sugarFree);
    }

    // Check for alcohol
    if (this.data?.alcoholContent) {
      pictograms.push(PICTOGRAMS.alcohol);
    }

    // Check for alcohol free
    if (alcoholFree) {
      pictograms.push(PICTOGRAMS.alcoholFree);
    }

    // Check for food suggestions
    const foodSuggestions = this.foodSuggestions;
    if (foodSuggestions?.length) {
      foodSuggestions.forEach((x) => {
        const suggestion = FOOD_SUGGESTION[x.id];
        if (suggestion) {
          pictograms.push(suggestion);
        }
      });
    }

    if (allergens.length) {
      // Check for fish allergens
      if (allergens.some((x: string) => ALLERGEN.FISH.includes(x))) {
        pictograms.push(PICTOGRAMS.allergenFish);
      }

      // Check for crustaceans allergens
      if (allergens.some((x: string) => ALLERGEN.CRUSTACEANS.includes(x))) {
        pictograms.push(PICTOGRAMS.allergenCrustaceans);
      }

      // Check for mollusc allergens
      if (allergens.some((x: string) => ALLERGEN.MOLLUSCS.includes(x))) {
        pictograms.push(PICTOGRAMS.allergenMolluscs);
      }

      // Check for gluten allergens
      if (allergens.some((x: string) => ALLERGEN.GLUTEN.includes(x))) {
        pictograms.push(PICTOGRAMS.allergenGluten);
      }

      // Check for nut allergens
      if (allergens.some((x: string) => ALLERGEN.NUTS.includes(x))) {
        pictograms.push(PICTOGRAMS.allergenNuts);
      }

      // Check for peanut allergens
      if (allergens.some((x: string) => ALLERGEN.PEANUTS.includes(x))) {
        pictograms.push(PICTOGRAMS.allergenPeanuts);
      }

      // Check for celery allergens
      if (allergens.some((x: string) => ALLERGEN.CELERY.includes(x))) {
        pictograms.push(PICTOGRAMS.allergenCelery);
      }

      // Check for mustard allergens
      if (allergens.some((x: string) => ALLERGEN.MUSTARD.includes(x))) {
        pictograms.push(PICTOGRAMS.allergenMustard);
      }

      // Check for egg allergens
      if (allergens.some((x: string) => ALLERGEN.EGGS.includes(x))) {
        pictograms.push(PICTOGRAMS.allergenEggs);
      }

      // Check for lactose allergens
      if (allergens.some((x: string) => ALLERGEN.LACTOSE.includes(x))) {
        pictograms.push(PICTOGRAMS.allergenLactose);
      }

      // Check for sesame allergens
      if (allergens.some((x: string) => ALLERGEN.SESAME.includes(x))) {
        pictograms.push(PICTOGRAMS.allergenSesame);
      }

      // Check for soy allergens
      if (allergens.some((x: string) => ALLERGEN.SOY.includes(x))) {
        pictograms.push(PICTOGRAMS.allergenSoy);
      }

      // Check for sulphite allergens
      if (allergens.some((x: string) => ALLERGEN.SULPHITES.includes(x))) {
        pictograms.push(PICTOGRAMS.allergenSulphites);
      }
    }

    const distinctPictograms = pictograms.filter(
      (x, i, arr) => arr.findIndex(t => t.pictogram === x.pictogram) === i
    );

    this.pictograms = distinctPictograms;

  }

  private _setRights() {
    if (this.isOwnProduct) {
      const canManageProducts = this._appState.hasRight([
        UserAction.ManageProducts
      ]);
      this.show.deleteButton = canManageProducts;
      this.show.addButton = canManageProducts;
      this.show.cloneButton = canManageProducts;
      this.show.labelsButton = this._formData.canExportProductLabels;
      this.show.sendForConformityConfirmationButton =
        (this._formData.conformityDeclarationSettings?.canSendForConfirmation ?? false) && !this.conformityInfo?.isConfirmed;

      this.show.sendToDirectoButton =
        canManageProducts && this._formData.canExport;
      this.show.getImageFromApollo =
        canManageProducts && this._formData.canExport;
    }
    this.show.pdfButton =
      !this.isMarketProduct && this._appState.hasModule(LxmAppModule.Pdf);
    this.show.validateButton = this._appState.hasModule(
      LxmAppModule.SellerPimExtensions
    );
    this.show.dotMenu =
      this.show.deleteButton ||
      this.show.addButton ||
      this.show.cloneButton ||
      this.show.pdfButton ||
      this.show.sendToDirectoButton;

    this.show.conformityInfo =
      this._appState.hasModule(LxmAppModule.ConformityDeclarations) &&
      this._appState.hasRight([UserAction.ViewConformityDeclarations]);

    this.show.confirmConformityButton =
      this._appState.hasModule(LxmAppModule.ConformityDeclarations) &&
      this._appState.hasRight([UserAction.ConfirmConformityDeclarations]);
  }

  public get data() {
    return this.state.dataSubject.value;
  }

  public get imagesList() {
    return this.data?.images;
  }

  public get mainImage() {
    return this.imagesList[0];
  }

  public get additionalImages() {
    return this.imagesList.filter((x) => x.id !== this.mainImage.id);
  }

  public get nutritionInfo() {
    return this.data?.markingAndNutritionInfo;
  }

  public get foodSuggestions() {
    return this.data?.foodSuggestions;
  }

  public get allergens() {
    return this.nutritionInfo?.allergens || [];
  }

  public get awards() {
    return this.data?.awards;
  }

  public openValidationDialog(
    validateOnOpen = false,
    validateId: string = null
  ) {
    this._action.openDialog(
      ProductValidationDialogComponent,
      {
        ...appSettings.DIALOG_SIZES.M,
        data: {
          routeData: this.route.snapshot.data,
          productId: this.productId,
          validateOnOpen: validateOnOpen,
          validateId: validateId
        }
      },
      () => { }
    );
  }

  public openExportProductsDialog(): void {
    if (!this.productId) {
      return;
    }

    const { countries, currencies, defaultCurrency } = this._formData || {};

    const data = {
      formData: {
        countries: countries,
        currencies: currencies,
        defaultCurrency: defaultCurrency
      },
      productIds: [this.productId]
    };

    this._action.openDialog(
      ExportProductsPdfDialog,
      {
        ...appSettings.DIALOG_SIZES.AUTO,
        data: data
      },
      (res) => {
        //
      }
    );
  }

  public clone() {
    const image = this.route.snapshot.data.productCard.images?.find(
      (x: { isMaster: boolean }) => x.isMaster == true
    );
    const prod = {
      productId: this.productId,
      name: this.route.snapshot.data.productCard.baseInfo.fullName,
      ean: this.route.snapshot.data.productCard.units.find(
        (x: { type: { id: ProductUnitType } }) =>
          x.type.id == ProductUnitType.BaseUnit
      ).defaultEan,
      imageUrl: image?.url,
      thumbnailUrl: image?.thumbnailSmallUrl,
      assortmentStatus: this.route.snapshot.data.productCard.assortments?.find(
        (x: { isCurrent: boolean }) => x.isCurrent == true
      )?.status
    };
    this._action.openDialog(
      ProductCloneDialogComponent,
      {
        data: {
          product: prod
        },
        ...appSettings.DIALOG_SIZES.M
      },
      (productId) => {
        if (!productId) {
          return;
        }
        this._router.navigate(["/products/clone", productId], {
          replaceUrl: true
        });
      }
    );
  }

  public delete() {
    const confirmOptions = this._action.createConfirm(
      "delete-modal-image",
      "cards.products.product_base_info.remove_product_title",
      "cards.products.product_base_info.remove_product_message"
    );

    this._action.confirm(confirmOptions, () => {
      this._productService.deleteProduct(this.productId).result(null, () => {
        this._router.navigate(["/products"]);
      });
    });
  }

  public exportProductLabelsDialog() {
    if (!this.productId) {
      return;
    }

    this._action.openDialog(
      ExportProductsLabelsDialog,
      {
        resolve: () =>
          this._productService
            .exportProductLabels(this.productId)
            .pipe(
              mergeMap(async (res) => {
                const blob = await this._productService
                  .downloadXls(res.id)
                  .toPromise();
                return { fileName: res.fileName, blob: blob };
              })
            )
            .pipe(
              map((res) => {
                const url = window.URL.createObjectURL(res.blob);
                const link = this._download.nativeElement;
                link.href = url;
                link.download = res.fileName;
                link.click();
                window.URL.revokeObjectURL(url);
              })
            )
      },
      (res) => {
        //
      }
    );
  }

  public sendToDirecto() {
    if (this.sendToDirectoButtonDisabled) {
      this._lxmMessage.error({
        message:
          "cards.products.product_base_info.product_info_is_not_filled_enough"
      });

      return;
    }

    const confirmOptions = this._action.createConfirm(
      "warning-modal-image",
      "cards.products.product_base_info.send_product_to_directo_title",
      "cards.products.product_base_info.send_product_to_directo_message"
    );

    this._action.confirm(confirmOptions, () => {
      this._productService.sendToDirecto(this.productId).result(
        null,
        (res) => {
          this._lxmMessage.ok({
            message:
              "cards.products.product_base_info.sent_product_to_directo_successful"
          });
        },
        (err) => {
          // this._lxmMessage.error({
          //   message: err.validationSummary || "cards.products.product_base_info.sent_product_to_directo_failed"
          // });
        }
      );
    });
  }

  public getImageFromApollo() {
    const confirmOptions = this._action.createConfirm(
      "warning-modal-image",
      "GetImageFromApollo",
      "You want to download image from Apollo"
    );

    this._action.confirm(confirmOptions, () => {
      this._productService.getImageFromApollo(this.productId).result(
        null,
        () => {
          this._lxmMessage.ok({
            message: "Image downloaded"
          });
          this._router.routeReuseStrategy.shouldReuseRoute = () => false;
          this._router.onSameUrlNavigation = "reload";
          this._router.navigate(["/products", this.productId]);
        },
        (err) => {
          // this._lxmMessage.error({
          //   message: err.validationSummary || "Image download failed."
          // });
        }
      );
    });
  }

  public getProductInfoFromApollo() {
    this._productService.getProductInfoFromApollo(this.productId).result(
      null,
      (res) => {
        this._lxmMessage.ok({ message: res.xml });
      },
      (err) => {
        // this._lxmMessage.error({
        //   message: err.validationSummary || "Error"
        // });
      }
    );
  }

  public confirmProductConformity() {
    const confirmOptions = this._action.createConfirm(
      "warning-modal-image",
      "cards.products.product_base_info.confirm_product_conformity_title",
      "cards.products.product_base_info.confirm_product_conformity_message"
    );

    this._action.confirm(confirmOptions, () => {
      this._productService.confirmProductConformity(this.productId).result(
        null,
        (res) => {
          this._lxmMessage.ok({
            message: "cards.products.product_base_info.product_conformity_confirmed",
          });
        },
        (err) => {

        }
      );
    });
  }

  public sendForConformityConfirmation() {
    this.sendForConformityConfirmationButtonDisabled = true;

    this._productService.sendForConformityConfirmation(this.productId).result(
      null,
      (res) => {
        this.sendForConformityConfirmationButtonDisabled = false;
        this._lxmMessage.ok({
          message: "cards.products.product_base_info.send_for_conformity_confirmation_success",
        });
      },
      (err) => {
        this.sendForConformityConfirmationButtonDisabled = false;
      });
  }

  ngAfterViewInit() {
    this._updateSubscription = this.state.dataSubject.subscribe(() =>
      this._updatePictograms()
    );
  }

  ngOnDestroy() {
    this.state.resetData();

    if (this.__hubConnection) {
      this.__hubConnection.off("productConformityInfoChanged");
    }

    if (this.__signalRSubscription) {
      this.__signalRSubscription.unsubscribe();
    }
  }
}
